import { extractImageFrames, clampTo } from '../../tools';
import type { baiduModelConfig } from '../types';

export const happyhorseConfigs: Record<string, baiduModelConfig> = {
    'happyhorse-1.1': {
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3029820
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3029821
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3030778
        apiPath: '/v3/aigc/bailian/api/v1/services/aigc/video-generation/video-synthesis',
        pollApiPath: '/v3/aigc/bailian/api/v1/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'output.task_id',
            statePath: 'output.task_status',
            success: ['SUCCEEDED'],
            failed: ['FAILED', 'CANCELED'],
            processing: ['PENDING', 'RUNNING', 'UNKNOWN'],
            videoUrlPath: 'output.video_url',
            errorPath: 'output.message',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            if (frameMode === 'first') {
                return {
                    model: 'happyhorse-1.1-i2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        media: [
                            {
                                type: 'first_frame',
                                url: firstImage,
                            },
                        ],
                    },
                    parameters: {
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            } else if (frameMode === 'ref') {
                return {
                    model: 'happyhorse-1.1-r2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        media: [
                            ...images.map(url => ({
                                type: 'reference_image',
                                url: url,
                            })),
                        ],
                    },
                    parameters: {
                        ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'] as const, '16:9'),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            } else {
                return {
                    model: 'happyhorse-1.1-t2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                    },
                    parameters: {
                        ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'] as const, '16:9'),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            }
        },
    },
    'happyhorse-1.1-v2v': {
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3030779
        apiPath: '/v3/aigc/bailian/api/v1/services/aigc/video-generation/video-synthesis',
        pollApiPath: '/v3/aigc/bailian/api/v1/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'output.task_id',
            statePath: 'output.task_status',
            success: ['SUCCEEDED'],
            failed: ['FAILED', 'CANCELED'],
            processing: ['PENDING', 'RUNNING', 'UNKNOWN'],
            videoUrlPath: 'output.video_url',
            errorPath: 'output.message',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, duration, aspectRatio, quality, providerOptions } = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            return {
                model: 'happyhorse-1.0-video-edit',
                input: {
                    prompt: prompt.slice(0, 5000),
                    media: [
                        {
                            type: 'video',
                            url: videoUri,
                        },
                        ...images.map(url => ({
                            type: 'reference_image',
                            url: url,
                        })),
                    ],
                },
                parameters: {
                    resolution: clampTo(quality || '720P', ['720P', '1080P'] as const, '720P'),
                    ratio: clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    audio_setting: typeof (providerOptions as any)?.audio_setting === 'string' ? (providerOptions as any).audio_setting : 'auto',
                    watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                },
            };
        },
    },
    'happyhorse-1.0': {
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3029820
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3029821
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3030778
        apiPath: '/v3/aigc/bailian/api/v1/services/aigc/video-generation/video-synthesis',
        pollApiPath: '/v3/aigc/bailian/api/v1/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'output.task_id',
            statePath: 'output.task_status',
            success: ['SUCCEEDED'],
            failed: ['FAILED', 'CANCELED'],
            processing: ['PENDING', 'RUNNING', 'UNKNOWN'],
            videoUrlPath: 'output.video_url',
            errorPath: 'output.message',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, duration, aspectRatio, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            if (frameMode === 'first') {
                return {
                    model: 'happyhorse-1.0-i2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        media: [
                            {
                                type: 'first_frame',
                                url: firstImage,
                            },
                        ],
                    },
                    parameters: {
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            } else if (frameMode === 'ref') {
                return {
                    model: 'happyhorse-1.0-r2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                        media: [
                            ...images.map(url => ({
                                type: 'reference_image',
                                url: url,
                            })),
                        ],
                    },
                    parameters: {
                        ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'] as const, '16:9'),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            } else {
                return {
                    model: 'happyhorse-1.0-t2v',
                    input: {
                        prompt: prompt.slice(0, 5000),
                    },
                    parameters: {
                        ratio: clampTo(aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:5', '5:4', '4:3', '3:4', '1:1'] as const, '16:9'),
                        resolution: clampTo((quality || '720P').toUpperCase(), ['720P', '1080P'] as const, '720P'),
                        duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                        watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                    },
                };
            }
        },
    },
    'happyhorse-1.0-v2v': {
        // https://bailian.console.aliyun.com/?tab=api#/api/?type=model&url=3030779
        apiPath: '/v3/aigc/bailian/api/v1/services/aigc/video-generation/video-synthesis',
        pollApiPath: '/v3/aigc/bailian/api/v1/tasks/{id}',
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'output.task_id',
            statePath: 'output.task_status',
            success: ['SUCCEEDED'],
            failed: ['FAILED', 'CANCELED'],
            processing: ['PENDING', 'RUNNING', 'UNKNOWN'],
            videoUrlPath: 'output.video_url',
            errorPath: 'output.message',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, duration, aspectRatio, quality, providerOptions } = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            return {
                model: 'happyhorse-1.0-video-edit',
                input: {
                    prompt: prompt.slice(0, 5000),
                    media: [
                        {
                            type: 'video',
                            url: videoUri,
                        },
                        ...images.map(url => ({
                            type: 'reference_image',
                            url: url,
                        })),
                    ],
                },
                parameters: {
                    resolution: clampTo(quality || '720P', ['720P', '1080P'] as const, '720P'),
                    ratio: clampTo(aspectRatio || '16:9', ['16:9', '4:3', '1:1', '3:4', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 5, [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    audio_setting: typeof (providerOptions as any)?.audio_setting === 'string' ? (providerOptions as any).audio_setting : 'auto',
                    watermark: typeof (providerOptions as any)?.watermark === 'boolean' ? (providerOptions as any).watermark : false,
                },
            };
        },
    },
};